import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AIChatbot from "@/components/AIChatbot";
import AuthCallback from "@/components/AuthCallback";
import MobileNavigation from "@/components/MobileNavigation";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { Toaster } from "@/components/ui/toaster";
import CodeOfConductGate from "@/components/CodeOfConductGate";
import Blog from "@/pages/Blog";
const BlogPost = lazy(() => import("@/pages/BlogPost"));
import { installSessionGuard } from "@/lib/sessionGuard";
installSessionGuard();
import Home from "@/pages/Home";
import Login from "@/pages/Login";
// Route-level code splitting — keeps the initial bundle small (PageSpeed:
// "reduce unused JavaScript"). Home + Login stay eager for instant first paint.
const About = lazy(() => import("@/pages/About"));
const Team = lazy(() => import("@/pages/Team"));
const Programs = lazy(() => import("@/pages/Programs"));
const TrainingVideos = lazy(() => import("@/pages/TrainingVideos"));
const BookSession = lazy(() => import("@/pages/BookSession"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Contact = lazy(() => import("@/pages/Contact"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Testimonials = lazy(() => import("@/pages/Testimonials"));
const SummerCamps = lazy(() => import("@/pages/SummerCamps"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
const Tournaments = lazy(() => import("@/pages/Tournaments"));
const ClaimAccount = lazy(() => import("@/pages/ClaimAccount"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const DataDeletion = lazy(() => import("@/pages/DataDeletion"));
const CoachBulkRate = lazy(() => import("@/pages/CoachBulkRate"));
const CampCheckout = lazy(() => import("@/pages/CampCheckout"));
const AdvancedCampCheckout = lazy(() => import("@/pages/AdvancedCampCheckout"));
const HighlightsReel = lazy(() => import("@/pages/HighlightsReel"));
import ErrorBoundary from "@/components/ErrorBoundary";
import * as serviceWorkerRegistration from "@/serviceWorkerRegistration";

// AppRouter handles the auth callback detection synchronously
function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  
  // Check for logged in user
  useEffect(() => {
    const checkUser = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setUser(data);
        }
      } catch (err) {
        console.log('Not logged in');
      }
    };
    checkUser();
  }, [location.pathname]);
  
  // CRITICAL: Check URL fragment synchronously during render (NOT in useEffect)
  // This prevents race conditions by processing session_id FIRST before other routes
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Handle Facebook OAuth callback
  if (location.pathname === '/auth/callback' || location.pathname === '/auth/facebook/callback') {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <main className="pb-16 md:pb-0"> {/* Add padding for mobile nav */}
        <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><div className="w-8 h-8 border-4 border-green-600 border-t-transparent rounded-full animate-spin" /></div>}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/team" element={<Team />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/videos" element={<TrainingVideos />} />
          <Route path="/book" element={<BookSession />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/highlights/:token" element={<HighlightsReel />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/claim-account/:token" element={<ClaimAccount />} />
          <Route path="/register" element={<Register />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/coach/bulk-rate" element={<CoachBulkRate />} />
          <Route path="/pricing" element={<Navigate to="/programs?tab=pricing" replace />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/testimonials" element={<Testimonials />} />
          <Route path="/summer-camps" element={<SummerCamps />} />
          <Route path="/camps/register" element={<CampCheckout />} />
          <Route path="/camps/register/advanced/:campId" element={<AdvancedCampCheckout />} />
          <Route path="/privacy-policy" element={<LegalPage contentType="privacy" />} />
          <Route path="/terms-of-service" element={<LegalPage contentType="terms" />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/legal/data-deletion" element={<DataDeletion />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth/facebook/callback" element={<AuthCallback />} />
          <Route path="/profile" element={<Dashboard />} />
          <Route path="/messages" element={<Dashboard />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      <Footer />
      <MobileNavigation user={user} />
      <AIChatbot />
      <PWAInstallPrompt />
    </>
  );
}

function App() {
  // Register service worker on app load.
  // Also proactively unregister any pre-existing service worker once per page
  // load if an URL param ?fresh=1 is present — gives the user + admin a quick
  // way to force-clear a broken cache after a deploy ("white screen" rescue).
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('fresh') === '1' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((regs) => {
          regs.forEach((r) => r.unregister());
          // Remove the param and hard-reload
          params.delete('fresh');
          const qs = params.toString();
          window.location.replace(window.location.pathname + (qs ? `?${qs}` : ''));
        });
        return;
      }
    } catch (_e) { /* ignore */ }
    serviceWorkerRegistration.register();
  }, []);

  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AppRouter />
          <CodeOfConductGate />
          <Toaster />
        </BrowserRouter>
      </ErrorBoundary>
    </div>
  );
}

export default App;
